import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getEnvConfig } from '@configs/env';
import styled from '@emotion/styled';
import { MenuItem, Select, useTheme } from '@mui/material';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import { ErrorIcon } from '@icons/ErrorIcon';
import { useGetSubscriptionConnections, useGetSubscriptionConnection } from '@queryHooks/useSubscriptionConnection';
import { useGetSMPConnections } from '@queryHooks/useTenant';
import type { ConnectorActionModel } from '@repositories/productConnectorRepository/models/ConnectorActionModel';
import { ActionConfig } from './ActionConfig';
import { InnerAction } from './InnerAction';

export interface ActionList {
  appName: string;
  subscriptionName: string | null;
  iconUrl: string;
  id: string;
  subscriptionId?: string;
}

export const ActionSelect = observer(() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const params = useParams();
  const { tenantId } = params;
  const { SMP_PROVIDER_NAME } = getEnvConfig();
  const { workflowDetailStore } = useStore();

  const [selectedAppId, setSelectedAppId] = useState<string>('');
  const [selectedActionId, setSelectedActionId] = useState<string>('');
  const [prevNodeId, setPrevNodeId] = useState<string | null>(null);
  const [showActionConfig, setShowActionConfig] = useState<boolean>(false);

  const { data: connections } = useGetSubscriptionConnections({
    productName: '',
  });
  const { data: smpConnection } = useGetSMPConnections(tenantId as string);
  const { data: connectionDetail } = useGetSubscriptionConnection(selectedAppId, {
    enabled: selectedAppId !== '' && selectedAppId !== String(SMP_PROVIDER_NAME),
  });

  const connectedActionLists = connections?.content
    .filter(con => con.status === 'CONNECTED')
    .filter(con => con.connection.connector.actions.length);

  /**
   * SMP 자체 액션 및 커넥션 액션 리스트를 가져오기
   */
  const actionLists: ActionList[] = useMemo(
    () => [
      {
        appName: t(`Provider_LOGO_${SMP_PROVIDER_NAME}`),
        subscriptionName: null,
        iconUrl: String(SMP_PROVIDER_NAME),
        id: String(SMP_PROVIDER_NAME),
      },
      ...(connectedActionLists?.map(con => ({
        appName: con.subscription.product.name,
        subscriptionName: con.subscription.name,
        iconUrl: con.subscription.product.iconUrl,
        id: con.subscription.id,
      })) ?? []),
    ],
    [connectedActionLists, SMP_PROVIDER_NAME, t],
  );

  // 선택된 앱의 액션 리스트 가져오기
  const selectedAppActions = useMemo(
    () =>
      selectedAppId === String(SMP_PROVIDER_NAME)
        ? smpConnection?.connector.actions ?? []
        : connectionDetail?.connection.connector.actions ?? [],
    [selectedAppId, SMP_PROVIDER_NAME, smpConnection, connectionDetail],
  );

  const isSMPAction = selectedAppId === String(SMP_PROVIDER_NAME);

  // 현재 노드 정보를 MobX로 자동 감지
  const currentNode = workflowDetailStore.findNode(workflowDetailStore.curNodeId ?? '');
  const currentNodeId = workflowDetailStore.curNodeId ?? '';
  const currentActionModel = currentNode?.data.actionModel;
  const currentConnectionModel = currentNode?.data.connectionModel;
  const currentIsSmpAction = currentNode?.data.isSmpAction;

  // 노드가 변경되면 Select 값 복원 (노드 ID 변경 감지)
  useEffect(() => {
    // 노드 ID가 변경되지 않았으면 아무것도 하지 않음
    if (prevNodeId === currentNodeId) {
      return;
    }

    // 현재 노드 ID 저장
    setPrevNodeId(currentNodeId);

    if (!currentNode) {
      setSelectedAppId('');
      setSelectedActionId('');
      setShowActionConfig(false);
      return;
    }

    // 액션이 설정되지 않은 새 노드인 경우 초기화
    if (!currentActionModel || !currentActionModel.actionMethod) {
      setSelectedAppId('');
      setSelectedActionId('');
      setShowActionConfig(false);
      return;
    }

    // 앱 ID 설정
    if (currentIsSmpAction) {
      setSelectedAppId(String(SMP_PROVIDER_NAME));
      setSelectedActionId(currentActionModel.actionMethod || '');
    } else if (currentConnectionModel?.subscription?.id) {
      setSelectedAppId(currentConnectionModel.subscription.id);
      setSelectedActionId(currentActionModel.actionMethod || '');
    }
    setShowActionConfig(true);
  }, [
    currentNodeId,
    prevNodeId,
    currentNode,
    currentActionModel,
    currentConnectionModel,
    currentIsSmpAction,
    SMP_PROVIDER_NAME,
  ]);

  const handleAppChange = (event: any) => {
    const { value: appId } = event.target;
    setSelectedAppId(appId);
    setSelectedActionId(''); // 앱이 변경되면 선택된 액션 초기화
    setShowActionConfig(false); // 액션 설정 화면 숨김
  };

  const handleActionChange = (event: any) => {
    const { value: actionMethod } = event.target;
    setSelectedActionId(actionMethod);

    const selectedAction = selectedAppActions.find(action => action.method === actionMethod);
    if (!selectedAction) return;

    handleClickAction(selectedAction);
  };

  const handleClickAction = (action: ConnectorActionModel) => {
    if (!workflowDetailStore.checkEditable()) return;

    const targetActionNode = workflowDetailStore.findNode(workflowDetailStore.curNodeId ?? '');
    if (!targetActionNode) return;

    runInAction(() => {
      if (isSMPAction) {
        // SMP 액션 처리
        targetActionNode.data.actionModel = {
          actionArguments:
            action.inputParameters.map(param => ({
              argumentName: param.name,
              expression: '',
              expressionType: param.inputType === 'ARRAY' ? 'CUSTOM' : 'SIMPLE',
            })) ?? [],
          actionMethod: action.method,
          actionName: action.name,
          connectionId: '',
        };

        targetActionNode.data.connectionModel = undefined;
        targetActionNode.data.isSmpAction = true;
      } else {
        // 일반 연결 액션 처리
        targetActionNode.data.actionModel = {
          ...targetActionNode.data.actionModel,
          type: 'CONNECTION_ACTION',
          actionArguments:
            action.inputParameters.map(param => ({
              argumentName: param.name,
              expression: '',
              expressionType: param.inputType === 'ARRAY' ? 'CUSTOM' : 'SIMPLE',
            })) ?? [],
          actionMethod: action.method,
          actionName: action.name,
          connectionId: connectionDetail?.connectionId ?? '',
        };

        targetActionNode.data.connectionModel = connectionDetail;
        targetActionNode.data.isSmpAction = false;
      }

      // ActionConfig 표시
      setShowActionConfig(true);
    });
  };

  return (
    <Container>
      <Title>{t('Workflow_Create_36')}</Title>
      <ActionContainer>
        <ToolbarName>{t('Workflow_Create_38')}</ToolbarName>
        <ActionSubTitle>{t('Workflow_Create_75')}</ActionSubTitle>
        {/* 1단계: 앱 선택 Select */}
        <Select
          size="small"
          value={selectedAppId}
          onChange={handleAppChange}
          sx={{
            minWidth: '100%',
            height: '64px',
            cursor: 'pointer',
            marginTop: '8px',
            '& .MuiSelect-select': {
              height: '64px !important',
              display: 'flex',
              alignItems: 'center',
              padding: '0 !important',
              boxSizing: 'border-box',
            },
            '& .MuiSelect-icon': {
              pointerEvents: 'none',
            },
          }}
          displayEmpty
          MenuProps={{
            disablePortal: true,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            slotProps: {
              paper: {
                sx: {
                  maxHeight: 415,
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                },
              },
            },
            PaperProps: {
              sx: {
                marginTop: '4px',
                '& .MuiList-root': {
                  padding: 0,
                },
              },
            },
          }}
          renderValue={selected => {
            if (!selected) {
              return (
                <div
                  style={{
                    width: '100%',
                    height: '64px',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 12px',
                    pointerEvents: 'none',
                    color: '#959DA5',
                    boxSizing: 'border-box',
                  }}
                >
                  {t('Workflow_Create_39')}
                </div>
              );
            }
            const selectedApp = actionLists.find(app => app.id === selected);
            return selectedApp ? <InnerAction action={selectedApp} /> : null;
          }}
        >
          {actionLists?.map(app => (
            <MenuItem
              key={app.id}
              value={app.id}
              sx={{
                padding: '12px 11px',
                minHeight: '64px',
                '&:hover': {
                  backgroundColor: theme => theme.colors['state-white-primary-hover'],
                },
                '&.Mui-selected': {
                  backgroundColor: theme => theme.colors['state-white-primary-pressed'],
                  '&:hover': {
                    backgroundColor: theme => theme.colors['state-white-primary-hover'],
                  },
                  '& strong, & p': {
                    color: theme => theme.colors['text-primary-light'],
                  },
                },
              }}
            >
              <InnerAction action={app} />
            </MenuItem>
          ))}
        </Select>

        {/* 2단계: 액션 선택 Select (앱이 선택되었을 때만 표시) */}
        {selectedAppId && selectedAppActions.length > 0 && (
          <Select
            size="small"
            value={selectedActionId}
            onChange={handleActionChange}
            sx={{
              minWidth: '100%',
              height: '64px',
              cursor: 'pointer',
              marginTop: '12px',
              '& .MuiSelect-select': {
                height: '64px !important',
                display: 'flex',
                alignItems: 'center',
                padding: '0 12px !important',
                boxSizing: 'border-box',
              },
              '& .MuiSelect-icon': {
                pointerEvents: 'none',
              },
            }}
            displayEmpty
            MenuProps={{
              disablePortal: true,
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              slotProps: {
                paper: {
                  sx: {
                    maxHeight: 390,
                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                  },
                },
              },
              PaperProps: {
                sx: {
                  marginTop: '4px',
                  '& .MuiList-root': {
                    padding: 0,
                  },
                },
              },
            }}
            renderValue={selected => {
              if (!selected) {
                return (
                  <div
                    style={{
                      width: '100%',
                      color: '#959DA5',
                    }}
                  >
                    {t('Workflow_Create_Action_Select')}
                  </div>
                );
              }
              const selectedAction = selectedAppActions.find(action => action.method === selected);
              return selectedAction ? (
                <ActionPreview>
                  <ActionName>{selectedAction.name}</ActionName>
                  <ActionDescription>{selectedAction.description}</ActionDescription>
                </ActionPreview>
              ) : null;
            }}
          >
            {selectedAppActions.map(action => (
              <MenuItem
                key={action.method}
                value={action.method}
                sx={{
                  padding: '11px 28px',
                  minHeight: '48px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  '&:hover': {
                    backgroundColor: theme => theme.colors['state-white-primary-hover'],
                  },
                  '&.Mui-selected': {
                    backgroundColor: theme => theme.colors['state-white-primary-pressed'],
                    '&:hover': {
                      backgroundColor: theme => theme.colors['state-white-primary-hover'],
                    },
                    '& strong, & p': {
                      color: theme => theme.colors['text-primary-light'],
                    },
                  },
                }}
              >
                <ActionName>{action.name}</ActionName>
                <ActionDesc>{action.description}</ActionDesc>
              </MenuItem>
            ))}
          </Select>
        )}

        {/* 액션 선택 후 ActionConfig 표시 */}
        {showActionConfig && <ActionConfig />}

        {connections?.totalElements === 0 && (
          <ErrorBox>
            <span>
              <ErrorIcon width={14.67} height={14.67} color={theme.colors['ic-primary']} />
            </span>
            <p>{t('Action_Select_01')}</p>
          </ErrorBox>
        )}
      </ActionContainer>
    </Container>
  );
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  background: inherit;
`;

const Title = styled.strong`
  ${fonts.Headline6};
  width: 100%;
  padding: 32px;
  border-bottom: 1px solid ${({ theme: { colors } }) => colors['border-gray-lighter']};
`;

const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 40px 32px;
`;

const ActionSubTitle = styled.strong`
  ${fonts.Body2};
  color: ${({ theme: { colors } }) => colors['text-gray-sub-dark']};
  margin: 4px 0 24px;
`;

const ToolbarName = styled.strong`
  display: flex;
  flex-direction: row;
  width: 100%;
  ${fonts.Headline7};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const ErrorBox = styled('div')`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px 0;
  margin-top: 24px;
  background-color: ${({ theme }) => theme.colors['bg-white']};
  border: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  border-radius: 5px;
  box-shadow: 0px 1px 0px 0px rgba(0, 0, 0, 0.02), 0px 0px 3px 0px rgba(0, 0, 0, 0.07);

  & span {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors['bg-primary-lighter']};
  }
  & p {
    ${fonts.Body2};
  }
`;

const ActionPreview = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  & strong {
    ${fonts.Headline8};
    color: ${({ theme: { colors } }) => colors['text-gray-main']};
  }
`;

const ActionName = styled.strong`
  ${fonts.Headline9};
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
  width: 100%;
`;

const ActionDesc = styled.p`
  ${fonts.Body4};
  color: ${({ theme: { colors } }) => colors['text-gray-sub-dark']};
  width: 100%;
  margin: 4px 0 0 0;
`;

const ActionDescription = styled.p`
  ${fonts.Body2};
  color: ${({ theme: { colors } }) => colors['text-gray-sub-dark']};
  transition: color 0.2s;
  margin: 0;
`;
